import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translations directly for immediate availability
import enTranslations from './locales/en.json';
import heTranslations from './locales/he.json';
import ruTranslations from './locales/ru.json';

// Translation resources - we'll load these dynamically
// This function will load translations based on the selected language
export const loadTranslations = async (language: SupportedLanguage): Promise<any> => {
  try {
    const translations = await import(`./locales/${language}.json`);
    i18n.addResourceBundle(language, 'translation', translations.default, true, true);
    return translations.default;
  } catch (error) {
    console.error(`Failed to load translations for ${language}:`, error);
    // Fallback to English if the requested language fails to load
    if (language !== 'en') {
      return loadTranslations('en');
    }
    return {};
  }
};

// Define supported languages
export const SUPPORTED_LANGUAGES = {
  en: 'English',
  he: 'עברית',
  ru: 'Русский'
} as const;

// Language flags mapping
export const LANGUAGE_FLAGS = {
  en: '🇺🇸',
  he: '🇮🇱', 
  ru: '🇷🇺'
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// RTL languages
export const RTL_LANGUAGES: SupportedLanguage[] = ['he'];

// Language detection configuration
const detectionOptions = {
  order: [
    'querystring',
    'cookie',
    'localStorage',
    'navigator',
    'htmlTag',
    'path',
    'subdomain'
  ],
  lookupQuerystring: 'lng',
  lookupCookie: 'i18next',
  lookupLocalStorage: 'i18nextLng',
  caches: ['localStorage', 'cookie'],
  excludeCacheFor: ['cimode'],
  checkWhitelist: true
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    debug: import.meta.env?.DEV || false,
    
    fallbackLng: 'en',
    supportedLngs: Object.keys(SUPPORTED_LANGUAGES),
    
    // Language detection
    detection: detectionOptions,
    
    // Interpolation
    interpolation: {
      escapeValue: false // React already does escaping
    },
    
    // Resources - loaded immediately with imports
    resources: {
      en: {
        translation: enTranslations
      },
      he: {
        translation: heTranslations
      },
      ru: {
        translation: ruTranslations
      }
    },
    
    // React specific options
    react: {
      useSuspense: false,
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'em']
    }
  });

export default i18n;

// Utility functions
export const isRTL = (language: string): boolean => {
  return RTL_LANGUAGES.includes(language as SupportedLanguage);
};

export const getDirection = (language: string): 'ltr' | 'rtl' => {
  return isRTL(language) ? 'rtl' : 'ltr';
};

export const changeLanguage = async (language: SupportedLanguage): Promise<void> => {
  // Translations are already loaded, just change the language
  await i18n.changeLanguage(language);
  
  // Update document direction
  document.documentElement.dir = getDirection(language);
  document.documentElement.lang = language;
  
  // Store in localStorage for persistence
  localStorage.setItem('i18nextLng', language);
};

// Initialize direction on app start
document.documentElement.dir = getDirection(i18n.language);
document.documentElement.lang = i18n.language;

// Initialize with default language
export const initializeI18n = async (): Promise<void> => {
  // Translations are already loaded via imports, just ensure direction is set
  const currentLanguage = i18n.language || 'en';
  document.documentElement.dir = getDirection(currentLanguage);
  document.documentElement.lang = currentLanguage;
};